import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import BeatLoader from "react-spinners/BeatLoader";
import {
  Box,
  Checkbox,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  DialogActions,
} from "@mui/material";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { toast } from "react-toastify";
import { useCallbackPrompt } from "../../../../../hooks/useCallbackPrompt";
import DialogBox from "../../../../../components/DialogBox";
import "../../StudySetup.css";
import {
  getReportLayoutById,
  getTranslationDataServices,
  saveTranslationsServices,
  UpdateSelectedReportService,
} from "../../../../../services/reports";
import { Button } from "@mui/material";
import { linkButton } from "../../Visits/ConfigureNavigations";
import RichTextEditor from "../../../../../components/RichTextEditor";
import { getLanguagesServices } from "../../Questionnaire/Questionnaire";
import { FaTimes } from "react-icons/fa";
import { stripHtml } from "../../../../../utils/dataMapping";
const EditSelectedRport = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const id = location.state?.id;

  const [load, setLoad] = React.useState(false);

  const [showDialog, setShowDialog] = React.useState(false);
  const [fieldData, setFieldData] = React.useState({});
  const [openModal, setOpenModal] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [editingHeader, setEditingHeader] = useState("");
  const [translations, setTranslations] = useState({});
  const [showPrompt, confirmNavigation, cancelNavigation] =
    useCallbackPrompt(showDialog);

  const editDispensationSchema = yup.object().shape({
    reportName: yup.string().required("This field is required"),
    ...Object.keys(fieldData || {}).reduce((schema, key) => {
      schema[key] = yup.string().required(`${key} is required`);
      return schema;
    }, {}),
  });

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(editDispensationSchema),
  });

  React.useEffect(() => {
    if (id) {
      fetchReportLayoutById(id);
    }
  }, [id]);

  React.useEffect(() => {
    getLanguagesData();
  }, []);

  const notify = () =>
    toast.success("Report Edited Successfully", {
      // theme: "colored",
      toastId: "form-creation",
    });

  const requestFailed = (msg) =>
    toast.error(`${msg}`, {
      // theme: "colored",
      toastId: "requestFailed",
    });

  const handleTranslationChange = (langCode, newValue) => {
    const strippedValue = stripHtml(newValue);

    setTranslations((prev) => {
      if (prev[langCode] === strippedValue) return prev;
      return { ...prev, [langCode]: strippedValue };
    });
  };

  const getLanguagesData = async (name) => {
    setLoad(true);
    try {
      const res = await getLanguagesServices(name);
      if (res?.status) {
        setLoad(false);
        const transformedLanguages = res?.data?.result
          .filter((item) => item?.isActive === true)
          .map((item) => ({
            ...item,
            languageCode: item.cultureName,
          }));

        setLanguages(transformedLanguages);
      }
    } catch (err) {
      setLoad(false);
      console.log("Error: ", err?.message);
    }
  };

  const getSelectedLanguagesData = async (name) => {
    setLoad(true);
    try {
      const res = await getTranslationDataServices(name);
      if (res?.status) {
        setLoad(false);

        const translationsObj = res.data.result.reduce((acc, item) => {
          acc[item.languageCode] = item.translationValue;
          return acc;
        }, {});

        setTranslations(translationsObj);
      }
    } catch (err) {
      setLoad(false);
      console.log("Error: ", err?.message);
    }
  };

  const handleOpenModal = (fieldKey) => {
    getSelectedLanguagesData(fieldKey);
    setEditingHeader(fieldKey);
    setOpenModal(true);
  };

  const fetchReportLayoutById = async (id) => {
    setLoad(true);
    try {
      const res = await getReportLayoutById(id);

      if (res.data) {
        setLoad(false);

        const {
          isBlinded,
          isActive,
          reportId,
          reportName,
          procName,
          id,
          createdAt,
          hasChart,
          translationData,
        } = res.data.result;

        const updatedProcName = procName.startsWith("rpt_")
          ? procName.replace("rpt_", "lbl")
          : procName;
        setValue("procName", procName);
        setValue("updatedProcName", updatedProcName);
        setValue("isBlinded", isBlinded);
        setValue("isActive", isActive);
        setValue("hasChart", hasChart);
        setValue("reportName", reportName);
        setValue("id", id);
        setValue("reportId", reportId);
        setValue("createdAt", createdAt);
        setFieldData(translationData);

        if (translationData) {
          Object.keys(translationData).forEach((key) => {
            setValue(key, translationData[key]);
          });
        }
        reset({
          isBlinded,
          isActive,
          hasChart,
          reportId,
          reportName,
          procName,
          updatedProcName: updatedProcName,
          createdAt,
          id,
          ...translationData,
        });
      }
    } catch (err) {
      console.log("Error: ", err.message);
      requestFailed(err.message);
    }
  };

  const textBoxBody = {
    marginLeft: "2px",
  };

  const textBoxStylesNew = {
    fontSize: 15,
    width: "320px",
    height: "5px",
    padding: "13px 14px",
  };

  const onSubmit = async (data) => {
    const {
      isBlinded,
      isActive,
      reportId,
      hasChart,
      reportName,
      procName,
      createdAt,
      id,
      ...translationData
    } = data;

    const updatedProcName = procName.startsWith("rpt_")
      ? procName.replace("rpt_", "lbl")
      : procName;

    const updatedTranslationData = {
      ...translationData,
      [updatedProcName]: reportName,
    };

    const updatedData = {
      isBlinded,
      isActive,
      hasChart,
      reportId,
      reportTitle: reportName,
      procName,
      createdAt,
      id,
      translationData: updatedTranslationData,
    };
    try {
      setLoad(true);
      setShowDialog(false);
      const res = await UpdateSelectedReportService(updatedData);
      if (res.status) {
        setLoad(false);
        notify();
        fetchReportLayoutById(id);
        // navigate("/study-management/study-settings/reports", {
        //   state: {
        //     isComeFromReportFrom: true,
        //   },
        // });
      }
    } catch (err) {
      console.log("Error: ", err.message);
      setLoad(false);
      setShowDialog(false);
      requestFailed(err.message);
    }
  };

  const handleBack = (e) => {
    e.preventDefault();
    if (isDirty) {
      const confirm = window?.confirm(
        "You have unsaved changes,Are you sure want to leave ?"
      );
      if (confirm) {
        navigate("/study-management/study-settings/reports", {
          state: {
            isComeFromReportFrom: true,
          },
        });
      } else {
        e.preventDefault();
        navigate("#", { state: { id: id } });
      }
    } else {
      navigate("/study-management/study-settings/reports", {
        state: {
          isComeFromReportFrom: true,
        },
      });
    }
  };

  const handleBackMain = (e) => {
    e.preventDefault();
    if (isDirty) {
      const confirm = window?.confirm(
        "You have unsaved changes,Are you sure want to leave ?"
      );
      if (confirm) {
        navigate("/study-management", {
          state: {
            isComeFromReportFrom: true,
          },
        });
      } else {
        e.preventDefault();
        navigate("#", { state: { id: id } });
      }
    } else {
      navigate("/study-management", {
        state: {
          isComeFromReportFrom: true,
        },
      });
    }
  };

  const rowStyles = {
    marginTop: "2%",
  };

  const labelStyles = {
    display: "flex",
    alignItems: "center",
    height: "30px",
  };

  const saveTranslations = async () => {
    setLoad(true);
    try {
      const translationsArray = Object.entries(translations).map(
        ([languageCode, translationValue]) => ({
          headerName: editingHeader ? editingHeader : "",
          translationValue: translationValue || "",
          languageCode: languageCode,
        })
      );

      const response = await saveTranslationsServices(translationsArray);
      if (response?.status) {
        setLoad(false);
        fetchReportLayoutById(id);
        toast.success("Translations saved successfully!");
      }
    } catch (err) {
      setLoad(false);
      console.log("Error:", err?.message);
      toast.error("Failed to save translations.");
    }
  };

  return (
    <>
      {load ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "70vh",
          }}
        >
          <BeatLoader color="#3661eb" />
        </div>
      ) : (
        <div className="content-body">
          <p className="study-management-link" style={{ fontWeight: "600" }}>
            <div
              onClick={(e) => {
                handleBackMain(e);
              }}
            >
              <Link>Manage Your Study</Link>
            </div>{" "}
            |{" "}
            <div
              onClick={(e) => {
                handleBack(e);
              }}
            >
              <Link>Reports</Link>
            </div>{" "}
            | <p className="study-edit-link">Edit Selected Report</p>-{" "}
            <b style={{ color: "gray" }}>
              {sessionStorage?.getItem("studyName")}
            </b>
          </p>

          <Row style={rowStyles}>
            <Col md={6}>
              <div className="study-management-head-start">
                <p className="study-management-heading">Edit Selected Report</p>
              </div>
            </Col>
            <Col md={6}></Col>
          </Row>

          <div>
            <DialogBox
              showDialog={showPrompt}
              confirmNavigation={confirmNavigation}
              cancelNavigation={cancelNavigation}
            />
          </div>

          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{ height: "auto", width: "100%" }}
            autoComplete="off"
          >
            <div className="createDispensationFormBody">
              <Row>
                <Col md={2}>
                  <p className="descriptionLabel" style={labelStyles}>
                    {"ProcName"}
                  </p>
                </Col>
                <Col md={10}>
                  <TextField
                    type="text"
                    disabled
                    value={getValues("procName") || ""}
                    style={textBoxBody}
                    inputProps={{ style: textBoxStylesNew }}
                    {...register("procName", {
                      onChange: (e) => {
                        setValue("procName", e.target.value, {
                          shouldValidate: true,
                        });
                        setShowDialog(true);
                      },
                    })}
                  />
                  {errors.procName && (
                    <div className="createCaregiverFlexEnd">
                      <div className="createCaregiverError">
                        <span className="error-text">
                          {errors.procName.message}
                        </span>
                      </div>
                    </div>
                  )}
                </Col>
              </Row>

              <Row>
                <Col md={2}>
                  <p className="descriptionLabel" style={labelStyles}>
                    {"ReportName"} <span className="error-highlight">*</span>
                  </p>
                </Col>
                <Col md={10}>
                  <div className="flex items-center gap-3">
                    <TextField
                      type="text"
                      style={textBoxBody}
                      inputProps={{ style: textBoxStylesNew }}
                      {...register("reportName")}
                      onBlur={(e) => {
                        setValue("reportName", e.target.value, {
                          shouldValidate: true,
                        });
                        setShowDialog(true);
                      }}
                    />
                    <span
                      style={linkButton}
                      onClick={() => {
                        handleOpenModal(getValues("updatedProcName") || "");
                      }}
                    >
                      Edit Translation
                    </span>
                  </div>
                  {errors.reportName && (
                    <div className="createCaregiverFlexEnd">
                      <div className="createCaregiverError">
                        <span className="error-text">
                          {errors.reportName.message}
                        </span>
                      </div>
                    </div>
                  )}
                </Col>
              </Row>

              <Row>
                <Col md={2}>
                  <p className="descriptionLabel" style={labelStyles}>
                    {"HasChart"}
                  </p>
                </Col>
                <Col md={10}>
                  <Checkbox
                    checked={getValues("hasChart") || false}
                    style={{ fontSize: "14px" }}
                    inputProps={{ "aria-label": "controlled" }}
                    {...register("hasChart", {
                      onChange: (e) => {
                        setValue("hasChart", e.target.checked, {
                          shouldValidate: true,
                        });
                        setShowDialog(true);
                      },
                    })}
                  />
                  {errors.hasChart && (
                    <p
                      style={{
                        marginTop: "10px",
                        color: "blue",
                      }}
                    >
                      {errors.hasChart.message}
                    </p>
                  )}
                </Col>
              </Row>

              <Row>
                <Col md={2}>
                  <p className="descriptionLabel" style={labelStyles}>
                    {"IsActive"}
                  </p>
                </Col>
                <Col md={10}>
                  <Checkbox
                    checked={getValues("isActive") || false}
                    style={{ fontSize: "14px" }}
                    inputProps={{ "aria-label": "controlled" }}
                    {...register("isActive", {
                      onChange: (e) => {
                        setValue("isActive", e.target.checked, {
                          shouldValidate: true,
                        });
                        setShowDialog(true);
                      },
                    })}
                  />
                  {errors.isActive && (
                    <p
                      style={{
                        marginTop: "10px",
                        color: "blue",
                      }}
                    >
                      {errors.isActive.message}
                    </p>
                  )}
                </Col>
              </Row>

              <Row>
                <Col md={2}>
                  <p className="descriptionLabel" style={labelStyles}>
                    {"IsBlinded"}
                  </p>
                </Col>
                <Col md={10}>
                  <Checkbox
                    checked={getValues("isBlinded") || false}
                    style={{ fontSize: "14px" }}
                    inputProps={{ "aria-label": "controlled" }}
                    {...register("isBlinded", {
                      onChange: (e) => {
                        setValue("isBlinded", e.target.checked, {
                          shouldValidate: true,
                        });
                        setShowDialog(true);
                      },
                    })}
                  />
                  {errors.isBlinded && (
                    <p
                      style={{
                        marginTop: "10px",
                        color: "blue",
                      }}
                    >
                      {errors.isBlinded.message}
                    </p>
                  )}
                </Col>
              </Row>

              {Object.keys(fieldData).map((key) => (
                <div key={key}>
                  <Row>
                    <Col md={2}>
                      <p className="descriptionLabel" style={labelStyles}>
                        {key} <span className="error-highlight">*</span>
                      </p>
                    </Col>
                    <Col md={10}>
                      <div className="flex items-center gap-3">
                        <TextField
                          style={textBoxBody}
                          inputProps={{ style: textBoxStylesNew }}
                          {...register(key)}
                          onBlur={(e) => {
                            setValue(key, e.target.value, {
                              shouldValidate: true,
                            });
                            setShowDialog(true);
                          }}
                        />
                        <span
                          style={linkButton}
                          onClick={() => {
                            handleOpenModal(key || "");
                          }}
                        >
                          Edit Translation
                        </span>
                      </div>
                      {errors[key] && (
                        <div className="createCaregiverFlexEnd">
                          <div className="createCaregiverError">
                            <span className="error-text">
                              {errors[key].message}
                            </span>
                          </div>
                        </div>
                      )}
                    </Col>
                  </Row>
                </div>
              ))}
            </div>
            <Row style={rowStyles}>
              <Col md={8}></Col>
              <Col md={2}>
                <div className="study-management-head-end">
                  <button
                    onClick={(e) => {
                      handleBack(e);
                    }}
                    className="study-management-cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </Col>
              <Col md={2}>
                <div className="study-management-head-end">
                  <button
                    type="submit"
                    className="study-management-create-btn-md"
                  >
                    Update
                  </button>
                </div>
              </Col>
            </Row>
          </Box>

          {openModal && (
            <div
              style={{ width: "1200px", height: "500px", fontWeight: "600" }}
            >
              <Dialog
                open={openModal}
                onClose={() => {}}
                sx={{ zIndex: 13000, top: "25px" }}
              >
                <DialogTitle
                  style={{
                    fontSize: "20px",
                    padding: "2px 13px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  Translation For : {editingHeader ? editingHeader : ""}
                  <FaTimes
                    onClick={() => setOpenModal(false)}
                    style={{
                      cursor: "pointer",
                      fontSize: "18px",
                      color: "gray",
                    }}
                  />
                </DialogTitle>
                <Divider />
                <h3
                  style={{
                    fontSize: "16px",
                    padding: "2px 7px",
                    margin: "7px 9px",
                  }}
                >
                  Languages:
                </h3>

                {languages.map((item) => (
                  <div
                    key={item.languageCode}
                    style={{ height: "400px", margin: "6px 20px" }}
                  >
                    <h4>{item.languageDisplay}</h4>
                    {console.log(
                      translations[item.languageCode],
                      item.languageCode,
                      "612"
                    )}
                    <DialogContent>
                      <div style={{ height: "248px" }}>
                        <RichTextEditor
                          value={translations[item.languageCode] || ""}
                          onChange={(newValue) =>
                            handleTranslationChange(item.languageCode, newValue)
                          }
                        />
                      </div>
                    </DialogContent>
                  </div>
                ))}
                <DialogActions>
                  <Button
                    size="large"
                    color="primary"
                    variant="outlined"
                    onClick={() => {
                      saveTranslations();
                      setOpenModal(false);
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    color="error"
                    onClick={() => {
                      setOpenModal(false);
                    }}
                  >
                    Cancel
                  </Button>
                </DialogActions>
              </Dialog>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default EditSelectedRport;
